// Which CHANNEL LANES a board renders — the pure half of the 9c fix.
//
// ⚠️⚠️ NARROWING THE DATA DOES NOT NARROW THE CHROME. The specialist boards
// scope their accounts, spine and asOf to one channel (channelScope.ts), so the
// other channel's numbers never enter their state at all — and the book table
// and the performance pulse still drew all three lanes, because nothing told
// them not to. What showed was a column of em-dashes beside a Blended lane that
// duplicated Meta exactly.
//
// ⚠️⚠️ BLENDED IS SUPPRESSED, NOT RELABELLED, ON A SINGLE-CHANNEL BOARD. With
// one channel in scope, blended spend IS that channel's spend and blended CPL
// IS its CPL — the lane is not a differently-named view of the same figures, it
// is the SAME figures a second time. Renaming it "Meta (total)" would keep a
// column of duplicates on screen and invite a reader to compare two numbers
// that cannot differ.
//
// ⚠️ THE CALLER DECLARES ITS CHANNELS AND THE PROP IS REQUIRED on both
// components, so a board added later cannot inherit three lanes by omission —
// which is exactly how this shipped.

import type { AdChannel } from '../../types/advertisingHome.js';

/** A lane is a family of three columns: spend · leads · CPL. */
export type LaneKey = AdChannel | 'blended';

/** Both channels, in the canonical order — what the AM board passes. */
export const ALL_AD_CHANNELS: readonly AdChannel[] = ['meta', 'google'];

/**
 * The lanes to render for a channel set, in canonical order.
 *
 * ⚠️ Blended appears only when there is genuinely something to blend (two or
 * more channels). An EMPTY channel set yields no lanes at all rather than a
 * lone Blended lane over nothing.
 */
export function resolveLanes(channels: readonly AdChannel[]): LaneKey[] {
  const lanes: LaneKey[] = [];
  for (const c of ALL_AD_CHANNELS) if (channels.includes(c)) lanes.push(c);
  if (lanes.length > 1) lanes.push('blended');
  return lanes;
}

/**
 * The group banding alternates by the lane's POSITION AMONG THOSE RENDERED,
 * never by its identity — so a single-channel board's one lane is still banded
 * off from the account/trend columns around it, and the AM board keeps its
 * existing Meta-tinted · Google-plain · Blended-tinted rhythm.
 */
export function laneIsTinted(index: number): boolean {
  return index % 2 === 0;
}
